<script setup lang="ts">
/* =============================================================
   FILTER INPUT
   =============================================================
   The search box every list panel puts in its #actions slot: an
   input with the magnifier inside it, and a label only a screen
   reader hears — sighted users read the placeholder, which says
   the same thing.

   A component rather than a class, unlike the data table beside
   it: this renders every node it styles, so scoped CSS reaches
   all of them, and the markup is four elements wired together
   rather than one element to opt in.

   `type="search"` for the browser's own clear button, and for
   the "search" key a phone keyboard then offers.

   The filtering itself stays on the page. What a list matches on
   is the page's business — this only holds the text.
   ============================================================= */

import Icon from '@/components/Icon.vue'

withDefaults(
  defineProps<{
    /** Announced to screen readers. The long form: "Filter berths by name, city…". */
    label: string
    /** Shown in the empty box. Falls back to the label when the two agree. */
    placeholder?: string
    modelValue?: string
  }>(),
  { modelValue: '' },
)

const emit = defineEmits<{ 'update:modelValue': [value: string] }>()
</script>

<template>
  <label class="filter">
    <span class="sr-only">{{ label }}</span>
    <Icon name="search" :size="16" class="icon" />
    <input
      :value="modelValue"
      type="search"
      :placeholder="placeholder ?? label"
      class="control"
      @input="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
    />
  </label>
</template>

<style scoped>
.filter {
  position: relative;
  display: flex;
  align-items: center;
}

.icon {
  position: absolute;
  inset-inline-start: var(--space-2xs);
  color: var(--color-text-faint);
  pointer-events: none;
}

.control {
  width: min(18rem, 40vw);
  height: var(--control-height-sm);
  /* Start inset: the icon's own inset, its 16px, then the same gap after it. */
  padding-inline: calc(var(--space-2xs) * 2 + 16px) var(--space-2xs);
  font-size: var(--text-sm);
  color: var(--color-text);
  background-color: var(--color-surface);
  border: var(--border-width) solid var(--color-border-strong);
  border-radius: var(--radius-sm);
}

.control:focus {
  outline: none;
  border-color: var(--color-accent);
  box-shadow: var(--shadow-focus);
}

@media (max-width: 40rem) {
  .filter {
    width: 100%;
  }

  .control {
    width: 100%;
  }
}
</style>
